/**
 * RBAC-ENF1 · C · 页面路径 → 进入该页需要的权限点（任意一个即可）。
 *
 * 用于：1) 侧栏按权限显示菜单；2) 直接输入网址 / 收藏夹进入没权限的页面时显示「没有权限访问这个页面」。
 * 匹配规则：按前缀、**最长前缀优先**（`/admin/pricing/tiers` 比 `/admin/pricing` 更具体）。
 * 没有出现在表里的路径（首页 / 通知 / 司导名单打印页等）= 登录即可。
 * 这里的权限点必须是后端注册表里真实存在的（有测试核对 `routePermissions.test.ts`）。
 */
export interface RoutePerm {
  prefix: string;
  anyOf: readonly string[];
}

const FINANCE_ANY = ["finance.invoices.list", "finance.reports.view", "finance.payables.list", "finance.receivables.list"] as const;

export const ROUTE_PERMS: readonly RoutePerm[] = [
  // 资源类
  { prefix: "/hotels", anyOf: ["hotels.hotels.list"] },
  { prefix: "/vehicles", anyOf: ["vehicles.vehicles.list"] },
  { prefix: "/drivers", anyOf: ["drivers.drivers.list"] },
  { prefix: "/attractions", anyOf: ["attractions.attractions.list"] },
  { prefix: "/suppliers", anyOf: ["suppliers.suppliers.list"] },
  { prefix: "/products", anyOf: ["products.products.list"] },
  { prefix: "/media-library", anyOf: ["products.products.list"] },
  { prefix: "/products/listing-jobs", anyOf: ["listing.jobs.view"] },
  { prefix: "/products/routes", anyOf: ["routes.config.view"] },
  // 商务类
  { prefix: "/orders", anyOf: ["orders.orders.list"] },
  { prefix: "/customers", anyOf: ["customers.customers.list"] },
  { prefix: "/ops/handoff", anyOf: ["handover.items.view"] },
  { prefix: "/ops/complaints", anyOf: ["complaints.complaints.list"] },
  { prefix: "/dispatch", anyOf: ["dispatch.center.view"] },
  { prefix: "/admin/channel-partners", anyOf: ["channel_partners.partners.list"] },
  { prefix: "/admin/channel-partners/approvals", anyOf: ["channel_partners.quotas.approve"] },
  { prefix: "/admin/channel-partners/open-api", anyOf: ["channel_partners.partners.status_manage"] },   // OPENAPI-2 · 审核 / 发正式密钥 / 吊销 = 老板 + 技术管理员 + 客户经理（同行详情「信用 & 结算」同一权限）
  { prefix: "/admin/channel-partners/settlements", anyOf: ["channel_partners.settlements.view"] },   // RBACFIX-1 · 只看结算 / 欠款（办理另需 process）
  { prefix: "/admin/pricing/customer-prices", anyOf: ["pricing.cost.view"] },   // PRICE-CUST-1 · 页面带成本，同后端读权限
  { prefix: "/admin/pricing", anyOf: ["pricing.policies.list"] },
  { prefix: "/admin/listing", anyOf: ["listing.mappings.view"] },
  // ═ COMPANY-STRIP BEGIN ═
  { prefix: "/marketing", anyOf: ["marketing.content.view"] },   // MKT-1 · 内容营销（订阅版）
  // ═ COMPANY-STRIP END ═
  // 运营类
  { prefix: "/admin/vehicle-operations", anyOf: ["vehicle_ops.ledger.view"] },
  { prefix: "/admin/finance", anyOf: FINANCE_ANY },
  { prefix: "/admin/api-integration", anyOf: ["api_integration.platforms.view"] },
  { prefix: "/ops/analytics", anyOf: ["analytics.dashboard.view"] },
  // 系统类
  { prefix: "/admin/permissions", anyOf: ["auth.roles.list", "auth.users.list"] },
  { prefix: "/admin/permissions/audit-logs", anyOf: ["auth.audit_logs.view"] },
  { prefix: "/admin/permissions/trash", anyOf: ["system.trash.manage"] },
  { prefix: "/admin/settings", anyOf: ["system.settings.view"] },
  { prefix: "/admin/settings/ai", anyOf: ["system.ai.view"] },
  { prefix: "/admin/settings/places", anyOf: ["media.library.manage"] },   // IMGLIB-1 · 地点清单：运营经理 / 老板 / 技术管理员
  { prefix: "/admin/site-payments", anyOf: ["site_payments.view"] },   // PAY-1 · 官网收款（OP 初筛 / 财务查账）
  { prefix: "/admin/settings/site-service", anyOf: ["system.settings.view"] },   // PAY-1 · 客服与收款（收款码卡片内再判 settings.payment_qr.manage）
  { prefix: "/admin/system/import", anyOf: ["system.import.manage"] },
  { prefix: "/admin/system/demo-tools", anyOf: ["system.demo_tools.manage"] },
  { prefix: "/admin/system/alerts", anyOf: ["system.alerts.view"] },
];

/** 侧栏菜单项 id → 需要的权限点（任意一个）。 */
export const NAV_PERMS: Readonly<Record<string, readonly string[]>> = {
  hotels: ["hotels.hotels.list"],
  vehicles: ["vehicles.vehicles.list"],
  drivers: ["drivers.drivers.list"],
  attractions: ["attractions.attractions.list"],
  suppliers: ["suppliers.suppliers.list"],
  products: ["products.products.list"],
  "media-library": ["products.products.list"],
  orders: ["orders.orders.list"],
  customers: ["customers.customers.list"],
  handoff: ["handover.items.view"],
  dispatch: ["dispatch.center.view"],
  "channel-partners": ["channel_partners.partners.list"],
  "open-api-approvals": ["channel_partners.partners.status_manage"],
  pricing: ["pricing.policies.list"],
  listing: ["listing.mappings.view"],
  // ═ COMPANY-STRIP BEGIN ═
  marketing: ["marketing.content.view"],   // MKT-1 · 另需订阅版 + 已开通（navConfig 的 flags.marketing）
  // ═ COMPANY-STRIP END ═
  "vehicle-operations": ["vehicle_ops.ledger.view"],
  finance: FINANCE_ANY,
  "api-integration": ["api_integration.platforms.view"],
  analytics: ["analytics.dashboard.view"],
  permissions: ["auth.roles.list", "auth.users.list"],
  settings: ["system.settings.view", "media.library.manage"],
  "site-payments-orders": ["site_payments.screen"],   // PAY-1 · 订单中心下的「官网收款」：OP 初筛
  "site-payments-finance": ["site_payments.audit"],   // PAY-1 · 财务下的「官网收款」：财务查账
  "import-tool": ["system.import.manage"],
  "demo-tools": ["system.demo_tools.manage"],
  "system-alerts": ["system.alerts.view"],
};

/** 找某个路径需要的权限点（最长前缀）；没有限制返回 null。 */
export function requiredPermsFor(pathname: string): readonly string[] | null {
  let best: RoutePerm | null = null;
  for (const r of ROUTE_PERMS) {
    if (pathname === r.prefix || pathname.startsWith(r.prefix + "/")) {
      if (!best || r.prefix.length > best.prefix.length) best = r;
    }
  }
  return best ? best.anyOf : null;
}
